if (import.meta.env.DEV) {
  void import("./wdyr");
}

import { toError } from "@/lib/to-error";
import { initPostHog } from "@/lib/posthog";
import { initSentry } from "@/lib/sentry";
import { AnalyticsProvider } from "@/components/analytics-provider";
import { AuthUIProviderTanstack } from "@papier/auth-ui/tanstack";
import { env } from "@papier/env/env-frontend";
import { ThemeProvider } from "@papier/ui/components/theme-provider";
import { Link, RouterProvider, createRouter, useRouter } from "@tanstack/react-router";
import { StrictMode } from "react";
import ReactDOM from "react-dom/client";
import { ErrorAlert } from "./components/error.tsx";
import { NotFound } from "./components/not-found.tsx";
import { authClient } from "./lib/auth-client.ts";
import { TanstackQueryProvider, queryClient } from "./lib/query.tsx";
import { orpc } from "./lib/orpc.ts";
import reportWebVitals from "./reportWebVitals.ts";
import { routeTree } from "./routeTree.gen.ts";
import { getUserAvatarUrl, uploadOrganizationLogo } from "./utils/image-upload.ts";
import { getCdnUrl } from "@papier/shared/utils/cdn-utils";
import { AvatarImage } from "@papier/ui/components/ui/avatar";
import { installChunkLoadRecovery } from "./lib/chunk-load-recovery";

import "./styles/global.css";

// Resolve raw R2 storage keys to CDN URLs when auth-ui renders
// user avatars and organization logos. Data and blob URLs pass through untouched.
function CdnAvatarImage({ src, alt, className }: { src: string; alt: string; className?: string }) {
  return <AvatarImage src={getCdnUrl(src)} alt={alt} className={className} />;
}

// Create a new router instance
installChunkLoadRecovery();
initPostHog();
initSentry();
const router = createRouter({
  routeTree,
  context: {
    queryClient,
    orpc,
    authClient,
  },
  defaultPreload: "intent",
  defaultNotFoundComponent: () => <NotFound />,
  defaultErrorComponent: ({ error, reset }) => (
    <ErrorAlert error={toError(error).message} onRetry={reset} />
  ),
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,
  Wrap: ({ children }) => {
    return (
      <ThemeProvider>
        <TanstackQueryProvider>{children}</TanstackQueryProvider>
      </ThemeProvider>
    );
  },
  InnerWrap: ({ children }) => {
    const router = useRouter();
    return (
      <AuthUIProviderTanstack
        authClient={authClient}
        baseURL={env.VITE_CLIENTSIDE_HOST}
        twoFactor={["totp"]}
        organization={{
          logo: {
            upload: async (file) => {
              return await uploadOrganizationLogo(file);
            },
            delete: async (url) => {
              // Delete logic can be added later if needed
              console.log("Delete organization logo:", url);
            },
          },
        }}
        credentials={{}}
        deleteUser
        emailVerification
        social={{
          providers: ["google"],
        }}
        onSessionChange={async () => {
          await queryClient.resetQueries();
          await router.invalidate();
        }}
        avatar={{
          upload: async (file) => {
            return await getUserAvatarUrl(file);
          },
          delete: async (url) => {
            // Delete logic can be added later if needed
            // For now, we just clear the user.image field
            console.log("Delete avatar:", url);
          },
          Image: CdnAvatarImage,
        }}
        navigate={(href) => router.navigate({ href })}
        replace={(href) => router.navigate({ href, replace: true })}
        Link={({ href, ...props }) => (
          <Link
            to={href}
            search={Object.fromEntries(new URLSearchParams(window.location.search))}
            {...props}
          />
        )}
      >
        <AnalyticsProvider>{children}</AnalyticsProvider>
      </AuthUIProviderTanstack>
    );
  },
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById("app");

if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <RouterProvider router={router} />
    </StrictMode>,
  );
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
